import { Box, LinearProgress, Typography } from '@mui/material';
import { OdmStatusCode } from 'interfaces/workspace';
import moment from 'moment';
import { FC, useEffect } from 'react';
import { StopwatchResult } from 'react-timer-hook';

interface LinearProgressWithLabelProps {
  value: number;
  status: OdmStatusCode | undefined;
  stopwatch: StopwatchResult;
  errorMessage: string;
}

const LinearProgressWithLabel: FC<LinearProgressWithLabelProps> = ({ value, status, stopwatch, errorMessage }) => {
  const { seconds, minutes, hours, pause } = stopwatch;

  // stop watch when success
  useEffect(() => {
    if (status === OdmStatusCode.COMPLETED) pause();
  }, [pause, status]);

  const formattedTime = moment(`${hours}:${minutes}:${seconds}`, 'H:m:s').format('HH:mm:ss');

  const renderStatusLabel = () => {
    if (errorMessage) return errorMessage;
    else {
      switch (status) {
        case OdmStatusCode.COMPLETED:
          return 'Success!';
        case OdmStatusCode.CANCELED:
          return 'Cancelled!';
        case OdmStatusCode.RUNNING:
        default:
          return 'Progressing';
      }
    }
  };

  const renderProcessingArea = (value: number) => (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: '12px' }}>
        <Typography>{`${status === OdmStatusCode.CANCELED ? '' : formattedTime}`}</Typography>
        <Typography sx={{ color: errorMessage ? 'red' : '' }}>{renderStatusLabel()}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mt: '8px' }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress variant="determinate" value={value} />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography>{`${value}%`}</Typography>
        </Box>
      </Box>
    </>
  );

  const renderQueuing = (
    <Box sx={{ width: '100%' }}>
      <Typography sx={{ mb: '8px' }}>{'Processing in another field!'}</Typography>
      <LinearProgress />
    </Box>
  );

  const renderWithStatus = () => {
    switch (status) {
      case OdmStatusCode.QUEUED:
        return renderQueuing;
      case OdmStatusCode.CANCELED:
        return renderProcessingArea(0);
      case OdmStatusCode.RUNNING:
      case OdmStatusCode.COMPLETED:
      default:
        return renderProcessingArea(value);
    }
  };
  return <>{renderWithStatus()}</>;
};

export default LinearProgressWithLabel;
